import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import SeoRegistryProvider from './seo/SeoRegistryProvider';
import { createRouteObjects, resolveLazyMatches } from './routes/routeObjects';
import { findPublicRoute } from './routes/publicRoutes';
import './index.css';

/** --- Legacy hash link migration (supports old #/album/... links) --- */
(function migrateHashToPath() {
  const { hash } = window.location; // e.g. "#/album/123?code=AAA"
  if (!hash) return;
  const m = hash.match(/^#\/(.+)/); // starts with "#/"
  if (!m) return;
  const newPath = '/' + m[1].replace(/^\/+/, ''); // ensure single leading "/"
  window.history.replaceState(null, '', newPath);
})();

async function boot() {
  const rootEl = document.getElementById('root');
  const routes = createRouteObjects({ includeAppRoutes: true });
  const pathname = window.location.pathname;

  // Public pages ship as complete HTML. Resolve the matched lazy page module
  // before the first paint so React can hydrate the prerendered markup instead
  // of replacing it (and so the router never renders a loading fallback first).
  let matches = [];
  try {
    matches = await resolveLazyMatches(routes, pathname);
  } catch {
    // Fall through: the router will retry the lazy import and show its fallback.
  }
  const leaf = matches[matches.length - 1]?.route;
  const prerenderedFor = rootEl.getAttribute('data-prerendered') || '';
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  const publicRoute = findPublicRoute(pathname);
  const prerendered =
    rootEl.hasChildNodes() &&
    ((publicRoute && prerenderedFor === normalizedPath) ||
      (prerenderedFor === '*' && leaf?.handle?.page === 'NotFound'));

  const router = createBrowserRouter(routes);
  const app = (
    <React.StrictMode>
      <SeoRegistryProvider>
        <RouterProvider router={router} />
      </SeoRegistryProvider>
    </React.StrictMode>
  );

  if (prerendered) {
    ReactDOM.hydrateRoot(rootEl, app);
  } else {
    rootEl.replaceChildren();
    ReactDOM.createRoot(rootEl).render(app);
  }
}

boot();

if ('serviceWorker' in navigator) {
  const registerMessagingWorker = () => {
    navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' }).catch(() => {});
  };

  if (document.readyState === 'complete') registerMessagingWorker();
  else window.addEventListener('load', registerMessagingWorker, { once: true });
}
navigator.serviceWorker?.addEventListener?.('message', (e) => {
  if (e.data?.type === 'DJ_NOTIFICATION_ACTION') {
    if ('clearAppBadge' in navigator) navigator.clearAppBadge();
    else if ('setAppBadge' in navigator) navigator.setAppBadge(0);
  }
  if (e.data?.type === 'DJ_NEW_REQUEST') {
    window.__unseenRequests = (window.__unseenRequests || 0) + 1;
    if ('setAppBadge' in navigator) navigator.setAppBadge(window.__unseenRequests);
  }
});
